import { useView } from '@hooks';
import { CreateCustomer, Customer } from '@services';
import { useStore } from '@stores';
import { Form } from 'antd';
import { observer } from 'mobx-react';
import React, { useCallback, useState } from 'react';
import { InvoiceAssignCustomerDrawerPresentation } from './InvoiceAssignCustomerDrawer.presentation';

interface IProps {
  open: boolean;
  invoiceId: string;
  onClose: () => void;
}

export const InvoiceAssignCustomerDrawer: React.FC<IProps> = observer(
  ({ open, invoiceId, onClose }) => {
    const { isMobile } = useView();
    const { invoiceStore, customerStore } = useStore();
    const [customerForm] = Form.useForm<CreateCustomer>();
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
    const [showCustomerForm, setShowCustomerForm] = useState(false);

    const handleClose = useCallback(() => {
      setSelectedCustomer(null);
      setShowCustomerForm(false);
      customerForm.resetFields();
      onClose();
    }, [customerForm, onClose]);

    const assign = useCallback(
      async (customerId: string) => {
        const result = await invoiceStore.assignCustomer({ invoiceId, customerId });
        if (result) handleClose();
      },
      [invoiceStore, invoiceId, handleClose]
    );

    const handleSelect = useCallback(
      (id: string) => setSelectedCustomer(customerStore.selectCustomers.get(id) ?? null),
      [customerStore]
    );

    const handleClearSelection = useCallback(() => setSelectedCustomer(null), []);

    const handleAddClick = useCallback(() => setShowCustomerForm(true), []);

    const handleCancelForm = useCallback(() => {
      setShowCustomerForm(false);
      customerForm.resetFields();
    }, [customerForm]);

    const handleAddressSearch = useCallback(
      (value: string) => customerForm.setFieldsValue({ postcode: value }),
      [customerForm]
    );

    const handleCreate = useCallback(
      async (values: CreateCustomer) => {
        const created = await customerStore.create(values);
        if (!created) return;
        // Kept selected so a failed assign can be retried without creating the customer twice.
        setSelectedCustomer(created);
        setShowCustomerForm(false);
        customerForm.resetFields();
        await assign(created.id);
      },
      [customerStore, customerForm, assign]
    );

    const handleAssign = useCallback(() => {
      if (selectedCustomer) assign(selectedCustomer.id);
    }, [selectedCustomer, assign]);

    return (
      <InvoiceAssignCustomerDrawerPresentation
        open={open}
        isMobile={isMobile}
        loading={invoiceStore.assignLoading || customerStore.loading}
        showCustomerForm={showCustomerForm}
        customerForm={customerForm}
        selectedCustomer={selectedCustomer}
        onClose={handleClose}
        onSelect={handleSelect}
        onClearSelection={handleClearSelection}
        onAddClick={handleAddClick}
        onCancelForm={handleCancelForm}
        onAddressSearch={handleAddressSearch}
        onCreate={handleCreate}
        onAssign={handleAssign}
      />
    );
  }
);
